import React, { useEffect, useState, useLayoutEffect, useRef } from "react";
import * as THREE from "three";
import { useThree, useFrame } from "@react-three/fiber";
import {
  InjectDrawCutsAndButtonsData,
  SetElasticsGLBData,
  SetGroup,
  StartExecution,
  PrepareCutsAndButtonsData,
} from "./Elastics";
import {
  computeBoundsTree,
  disposeBoundsTree,
  acceleratedRaycast,
  MeshBVH,
} from "three-mesh-bvh";
import { placeIPRBetweenTeeth, updateIPRBetweenTeeth } from "@/helpers/ipr";
import { GUI } from "dat.gui";
import { getParams } from "@/helpers/getParams";
import { useGlobalStore } from "../../store";
import {
  PrepareTeethMovementPath,
  areColliding,
  calculateTeethPositions,
} from "./teethControls/TeethPositionHelper";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import {
  addRemoveAttachment,
  onPointerMove,
  selectTooth,
  attachmentMaterial as newAttachmentMaterial,
} from "./teethControls/addRemoveAttachment";
import {
  assignPonticsMaterial,
  assignPonticsToGroup,
  ponticsTeethMaterial,
} from "./controls/Pontics";
import { globalBiteJump, globalMaxStepNumber, isBJ } from "./Scene";
import { useCustomTranslation } from "@/hooks/useTranslation";
import { isComparingMode } from "./modifications/ComparePlans";
import { isAppleMobile, isIpad } from "@/helpers/detectBrowser";
import MeasurementTool from "./controls/measurementTool.ts";
export const measurementTool = new MeasurementTool();

import CustomRotationControls from "./controls/customControls";
import { TeethHeatmapGenerator } from "./Heatmaps";
import { OcclusalArea, OcclusalSceneController } from "@/helpers/occ";
import { occlusalSplitGroup } from "./Split";
export const Controller = new CustomRotationControls();
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

let BJGroup;
let optionCounter = 0;
export let teethMeshObject = {};

let isElasticsGroupAssigned = false,
  isPonticsGroupAssigned = false;

let once = 0;
let guiCounter = 0;
let conf = { color: "#fff9f9" }; //teeth color
let conf1 = { color: "#2e2e2e" }; // specular color
let conf5 = { color: "#303030" }; // emissive gum color
let conf2 = { color: "#ff9090" }; // gum color
let conf3 = { color: "#ff93ab" }; // emissive color
let conf4 = { color: "#ffe2e6" }; // Spec gum color
const turnGUIOn = false;
let rootsAreCreated = false;
const turnCutsOn = false;
let isIPRPlaced = false;
export let isAdvancedHeatmaps = false;
export function setIsAdvancedHeatmaps(state) {
  isAdvancedHeatmaps = state;
}
export let heatMapManager;
export let occlusalAreaManager, occlusalSceneController;
export let splitScene, mainScene;

let oldOptions = {};
let orbitControlSwitch = true;
let attachmentsList = [];
let globalCurrentStep;
let selectedAttachment;
// let selectedAttachmentOffset;
const tempParent = new THREE.Group();
const geometry = new THREE.BufferGeometry();
const line = new THREE.Line(geometry, new THREE.LineBasicMaterial());
export let attachmentMaterial = new THREE.MeshPhysicalMaterial({
  color: 0xef8e9a,
  roughness: 0,
  transparent: false,
  side: THREE.DoubleSide,
});
let gumMaterial;

let meshGroup;
let cameraRotationListener;
export let teethMaterial,
  defaultTeethMaterial,
  occlusalTeethMaterial,
  selectedTeethMaterial,
  lockedTeethMaterial;
export let tpWithoutBiteJumpWithMod2 = [];
export let boundingTreeCount = 1;
export function disposeObject(object) {
  object.geometry.dispose();
  object.material.dispose();
}
export function replaceLastStepWithLastOriginalStep(groupRef, groupRef2) {
  const maxStepNumber = groupRef2.current.children.length / 2 - 1;

  const tpBiteJumpClone0 = tpWithoutBiteJumpWithMod2[0];
  const tpBiteJumpClone1 = tpWithoutBiteJumpWithMod2[1];
  tpBiteJumpClone0.parent = groupRef.current;
  tpBiteJumpClone0.visible = true;
  groupRef.current.children[maxStepNumber * 2] = tpBiteJumpClone0;

  tpBiteJumpClone1.parent = groupRef.current;
  tpBiteJumpClone1.visible = true;
  groupRef.current.children[maxStepNumber * 2 + 1] = tpBiteJumpClone1;
}
/**
 *
 * @param {Camera} camera
 * @param {String} listenerName
 * @param {String} listenerGroup
 * @returns {Bool}
 */
export function checkCameraListenerExistence(
  camera,
  listenerName,
  listenerGroup,
) {
  let state = false;
  for (
    let index = 0;
    index < camera._listeners[listenerGroup].length;
    index++
  ) {
    if (camera._listeners[listenerGroup][index].name == listenerName) {
      state = true;
      break;
    }
  }
  return state;
}

export function isVisible(object, traverses = 10) {
  let isVisibleResult = object.visible;
  let parentIndex = 1;
  let parent = object.parent;
  while (isVisibleResult && parent && parentIndex < traverses) {
    isVisibleResult = parent.visible;
    parent = parent.parent;
    parentIndex++;
  }
  return isVisibleResult;
}

function Mesh({
  tploaded,
  view,
  maxStepNumber,
  GLBData: GLBData,
  perspectiveCameraRef,
  controlsRef,
  iprOverLays,
  setIprOverlays,
  numberingOverLays,
  model,
  setModel,
  isViewerControlsEnabled,
  teethTransformationData,
  ponticsData,
  setAutoSave,
  biteJumpsSteps,
}) {
  const { dentalNotation } = getParams();
  const groupRef = React.useRef(null);

  const {
    canvas,
    setRefs,
    setControls3D,
    groupRef2,
    selectedTooth,
    selectedAttachmentType,
    toggle,
    currentStep,
    logs,
    setLogs,
    setScene,
    attachmentArray,
    setToggle,
  } = useGlobalStore(state => ({
    selectedAttachmentType: state.controls3D.selectedAttachmentType,
    selectedTooth: state.controls3D.selectedTooth,
    setRefs: state.setRefs,
    groupRef2: state.refs.groupRef2,
    canvas: state.refs.canvas,
    controlsRef: state.refs.controlsRef,
    setControls3D: state.setControls3D,
    toggle: state.toggle,
    currentStep: state.stepperControls.currentStep,
    logs: state.logs,
    setLogs: state.setLogs,
    setScene: state.setScene,
    attachmentArray: state.controls3D.attachmentArray,
    setToggle: state.setToggle,
  }));

  if (!teethMaterial) {
    defaultTeethMaterial = new THREE.MeshPhysicalMaterial({
      roughness: 0.4,
      reflectivity: 8,
      specularIntensity: 3,
      specularColor: 0x2e2e2e,
      emissiveIntensity: 1,

      color: conf.color,
      emissive: conf5.color,
      side: THREE.DoubleSide,
      flatShading: isAppleMobile() ? true : false,
      clearcoat: 0.8,
      name: "default",
      vertexColors: false,
      // transparent: true,
      // opacity: 0.5,
    });
    teethMaterial = defaultTeethMaterial.clone();

    occlusalTeethMaterial = new THREE.MeshPhysicalMaterial({
      roughness: 0.4,
      reflectivity: 0,
      specularIntensity: 0,
      specularColor: 0x2e2e2e,
      emissiveIntensity: 0,

      color: conf.color,
      emissive: conf5.color,
      side: THREE.DoubleSide,
      flatShading: false,
      clearcoat: 0.8,
      // renderOrder: 10,
      name: "Occlusal Teeth Material",
      vertexColors: true,
      // transparent: true,
      // opacity: 0.5,
    });

    assignPonticsMaterial(
      new THREE.MeshPhysicalMaterial({
        roughness: 0.4,
        reflectivity: 4,
        specularIntensity: 1.5,
        specularColor: 0x2e2e2e,
        emissiveIntensity: 1,

        color: 0xbdbdbd,
        emissive: conf5.color,
        side: THREE.DoubleSide,
        flatShading: false,
        clearcoat: 0.8,
      }),
    );
  }
  if (!selectedTeethMaterial) {
    selectedTeethMaterial = new THREE.MeshPhysicalMaterial({
      roughness: 0.4,
      reflectivity: 0,
      specularIntensity: 2,
      specularColor: "#FFFF8B",
      emissiveIntensity: 5,
      //   emissive: conf5.color,
      color: 0xc2e2ff,
      side: THREE.DoubleSide,
      flatShading: false,
      clearcoat: 0.8,
      name: "selected",
      vertexColors: false,
    });
  }
  if (!lockedTeethMaterial) {
    lockedTeethMaterial = new THREE.MeshPhysicalMaterial({
      roughness: 0.4,
      reflectivity: 4,
      specularIntensity: 3,
      specularColor: 0x2e2e2e,
      emissiveIntensity: 1,
      // emissive: conf5.color,
      color: "darkgray",
      side: THREE.DoubleSide,
      flatShading: false,
      clearcoat: 0.8,
      name: "locked",
    });
  }
  attachmentMaterial.color.setHex(0xef8e9a);

  React.useEffect(() => {
    setRefs({
      groupRef,
    });
  }, []);

  if (!optionCounter) {
    oldOptions = toggle;
    optionCounter++;
  }

  if (groupRef.current) {
    if (!isElasticsGroupAssigned) {
      isElasticsGroupAssigned = true;
      SetGroup(groupRef, groupRef2);
      SetElasticsGLBData(GLBData);
      PrepareCutsAndButtonsData();
      InjectDrawCutsAndButtonsData();
      if (boundingTreeCount === 0) {
        StartExecution();
      } else {
        console.log("this is counter ", boundingTreeCount);
      }
    }
    if (!isPonticsGroupAssigned) {
      isPonticsGroupAssigned = true;
      assignPonticsToGroup(groupRef);
    }
  }
  if (toggle.occ) {
    ponticsTeethMaterial.side = THREE.FrontSide;
    ponticsTeethMaterial.needsUpdate = true;
    selectedTeethMaterial.vertexColors = true;
    teethMaterial.copy(occlusalTeethMaterial);
    selectedTeethMaterial.needsUpdate = true;
  } else {
    ponticsTeethMaterial.transparent = false;
    ponticsTeethMaterial.side = THREE.DoubleSide;
    ponticsTeethMaterial.opacity = 1;
    ponticsTeethMaterial.needsUpdate = true;
    selectedTeethMaterial.needsUpdate = true;

    selectedTeethMaterial.vertexColors = false;
    teethMaterial.copy(defaultTeethMaterial);
    selectedTeethMaterial.needsUpdate = true;
  }
  if (toggle.numbering) {
    attachmentMaterial.transparent = true;
    attachmentMaterial.opacity = 0.5;
    newAttachmentMaterial.opacity = 0.5;
    newAttachmentMaterial.needsUpdate = true;
    attachmentMaterial.side = THREE.DoubleSide;
    attachmentMaterial.needsUpdate = true;
    newAttachmentMaterial.opacity = 0.5;
    newAttachmentMaterial.transparent = true;
    newAttachmentMaterial.opacity = 0.5;
    newAttachmentMaterial.side = THREE.DoubleSide;
    newAttachmentMaterial.needsUpdate = true;
  } else {
    attachmentMaterial.transparent = false;
    attachmentMaterial.side = THREE.DoubleSide;
    attachmentMaterial.opacity = 1;
    newAttachmentMaterial.needsUpdate = true;
    attachmentMaterial.needsUpdate = true;
    newAttachmentMaterial.transparent = false;
    newAttachmentMaterial.side = THREE.DoubleSide;
    newAttachmentMaterial.opacity = 1;
    newAttachmentMaterial.needsUpdate = true;
  }

  if (toggle.attachment) {
    attachmentMaterial.visible = true;
    newAttachmentMaterial.visible = true;
  } else {
    attachmentMaterial.visible = false;
    newAttachmentMaterial.visible = false;
  }
  if (!gumMaterial) {
    gumMaterial = new THREE.MeshPhysicalMaterial({
      roughness: 0.35,
      reflectivity: 0.65,
      specularIntensity: 0.35,
      specularColor: conf4.color,
      color: conf2.color,
      emissive: conf3.color,
      emissiveIntensity: 0.15,
      side: THREE.DoubleSide,
      opacity: toggle.show3DControls ? 0 : 1,
      depthWrite: toggle.show3DControls ? false : true,
      transparent: toggle.show3DControls ? true : false,
    });
  } else {
    gumMaterial.transparent = toggle.show3DControls ? true : false;
    gumMaterial.opacity =
      toggle.show3DControls && !toggle.showContModification ? 0 : 1;
    gumMaterial.depthWrite =
      toggle.show3DControls && !toggle.showContModification ? false : true;
  }

  const centering_group = new THREE.Group();
  const scene = useThree().scene;
  const camera = useThree().camera;
  const controls = useThree().controls;
  const pointer = new THREE.Vector2();
  let raycaster = new THREE.Raycaster();
  const occlusalMainGroup = new THREE.Group();
  occlusalMainGroup.name = "occ_group";
  const lineGeometry = new THREE.BufferGeometry();
  const lineOcc = new THREE.LineSegments(
    lineGeometry,
    new THREE.LineBasicMaterial({ color: 0xe91e63 }),
  );
  function myCallbackFunction() {
    reflectIPRVision();
  }
  if (controlsRef?.current) {
    if (controlsRef.current.hasListener) {
      controlsRef.current.removeEventListener("update", cameraRotationListener);
      controlsRef.current.addEventListener("update", myCallbackFunction);
      cameraRotationListener = myCallbackFunction;
    } else {
      controlsRef.current.addEventListener("update", myCallbackFunction);
      cameraRotationListener = myCallbackFunction;
      controlsRef.current.hasListener = true;
    }
  }

  useEffect(() => {
    //console.log("wat?");
    setTimeout(() => {
      myCallbackFunction();
    }, 500);
  }, [toggle.showUpper, toggle.showLower, view]);

  globalCurrentStep = currentStep;
  if (!meshGroup) {
    meshGroup = PrepareMesh(GLBData, ponticsData);

    // console.log(meshGroup.children.length, maxStepNumber, "there u are");

    let gum;
    const startTime1 = Date.now();

    if (isAdvancedHeatmaps) {
      heatMapManager = new TeethHeatmapGenerator();
    } else {
      occlusalAreaManager = new OcclusalArea();
      occlusalSceneController = new OcclusalSceneController(
        [occlusalMainGroup, occlusalSplitGroup],
        globalMaxStepNumber,
      );
      // scene.updateMatrixWorld(true);
    }
    const length = meshGroup.children.length;
    for (let index = 0; index < length; index++) {
      gum = meshGroup.children[index];

      if (!gum || !gum.geometry) {
        continue;
      }

      if (index < length - (isAdvancedHeatmaps ? 4 : 0)) {
        if (!gum.userData["boundsTree"]) {
          gum.userData["boundsTree"] = true;
          prepareBoundingTrees(gum.geometry);
        } else {
          gum.userData["boundsTree"] = true;
        }
        gum.children.forEach(tooth => {
          if (tooth.geometry.boundsTree) {
            tooth.userData["boundsTree"] = true;
          }
          if (!tooth.userData["boundsTree"]) {
            tooth.userData["boundsTree"] = true;
            prepareBoundingTrees(tooth.geometry);
          }
        });
      }
      if (heatMapManager && index % 2 === 1) {
        heatMapManager.addTask(
          meshGroup.children[index - 1].children,
          meshGroup.children[index].children,
        );
      }
    }
    console.log(Date.now() - startTime1, " this is the timer");
    boundingTreeCount--;
  }

  /**
   * Hides and unhide IPRs based on angle between the cam and the IPR
   */

  useEffect(() => {
    measurementTool.attach({
      scene,
      camera,
      controls: controlsRef.current,
      canvas: canvas.current,
    });
    measurementTool.addNewObjectsToCheck([
      groupRef?.current?.children[currentStep * 2],
      groupRef?.current?.children[currentStep * 2 + 1],
    ]);
    for (let isLower = 0; isLower < 2; isLower++) {
      groupRef?.current?.children[currentStep * 2 + isLower].children.forEach(
        tooth => {
          measurementTool.addNewObjectToCheck(tooth);
          tooth.children[0].children.forEach(att => {
            measurementTool.addNewObjectToCheck(att);
          });
        },
      );
    }
    return () => {
      measurementTool.dispose();
    };
  }, [scene, camera, controls, groupRef, currentStep]);

  useEffect(() => {
    if (!mainScene) {
      mainScene = scene;
    } else {
      console.error("scene reloaded");
    }
  }, [scene]);
  useEffect(() => {
    if (groupRef2.current) {
      splitScene = groupRef2.current.parent;
      splitScene.add(occlusalSplitGroup);
    }
  }, [groupRef2]);

  useEffect(() => {
    if (groupRef.current) {
      scene.add(occlusalMainGroup);
    }
  }, [groupRef]);
  function prepareBoundingTrees(geometry) {
    geometry.computeBoundsTree({ indirect: true });
  }

  function reflectIPRVision() {
    if (!toggle.ipr || !toggle.hasIpr) {
      return;
    }
    const angleAllowed = 90;
    const allowedYOffset = 100;
    let inc = 1;
    let gumCenter = new THREE.Vector3(),
      tempCasher = new THREE.Vector3();
    let state = false;
    for (let step = currentStep * 2; step <= currentStep * 2 + 1; step++) {
      for (
        let toothIndex = 0;
        toothIndex < groupRef.current.children[step].children.length;
        toothIndex++
      ) {
        for (
          let iprIndex = 0;
          iprIndex <
          groupRef.current?.children[step]?.children[toothIndex]?.children[2]
            ?.children.length;
          iprIndex++
        ) {
          if (inc === 1) {
            inc = 2;
            const box3 = new THREE.Box3().setFromObject(
              groupRef.current.children[0],
            );
            box3.getCenter(gumCenter);
          }
          const box3 = new THREE.Box3().setFromObject(
            groupRef.current.children[step].children[toothIndex].children[2],
          );
          box3.getCenter(tempCasher);

          const yResult = Math.abs(
            (Math.atan2(camera.position.x, camera.position.z) -
              Math.atan2(gumCenter.x, gumCenter.z)) *
              (180 / Math.PI) -
              (Math.atan2(tempCasher.x, tempCasher.z) -
                Math.atan2(gumCenter.x, gumCenter.z)) *
                (180 / Math.PI),
          );
          if (toggle.showUpper !== toggle.showLower) {
            if (
              groupRef.current.children[step].children[
                toothIndex
              ].children[2].name.charAt(0) === "u"
            ) {
              groupRef.current.children[step].children[
                toothIndex
              ].children[2].visible = toggle.showLower;
              state = toggle.showLower;
              continue;
            }
            if (
              groupRef.current.children[step].children[
                toothIndex
              ].children[2].name.charAt(0) === "l"
            ) {
              groupRef.current.children[step].children[
                toothIndex
              ].children[2].visible = toggle.showUpper;
              state = toggle.showUpper;
              continue;
            }
          }
          if (
            yResult > angleAllowed &&
            Math.abs(camera.position.y) < allowedYOffset
          ) {
            groupRef.current.children[step].children[
              toothIndex
            ].children[2].visible = false;
            state = false;
          } else {
            groupRef.current.children[step].children[
              toothIndex
            ].children[2].visible = toggle.ipr || !toggle.hasIpr;
            state = true;
          }
        }
      }
    }
  }

  function createRootAnchors(teethTransformationData) {
    const loader = new GLTFLoader();
    loader.load("RootAnchors.glb", function (rootAnchor) {
      const maxStepNumber = GLBData.length - 1;
      const parentUpper = groupRef.current.children[maxStepNumber * 2];
      const parentLower = groupRef.current.children[maxStepNumber * 2 + 1];
      let cloneHolder;

      parentUpper.children.forEach(item => {
        let teethNumberMinusOne = item.name.split("_")[2].substring(1) - 1;
        if (teethTransformationData[teethNumberMinusOne]) {
          cloneHolder = rootAnchor.scene.children[0].clone();
          cloneHolder.scale.set(100, 100, 100);
          cloneHolder.name = "rootAnchor";
          cloneHolder.updateMatrixWorld();
          const geometry = new THREE.BoxGeometry(1, 1, 1);
          const material = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
          const cube = new THREE.Mesh(geometry, material);
          const cube1 = new THREE.Mesh(geometry, material);
          cube.position.set(
            teethTransformationData[teethNumberMinusOne]
              .ToothRotationCenter[0] + parentUpper.parent.position.x,
            teethTransformationData[teethNumberMinusOne]
              .ToothRotationCenter[1] + parentUpper.parent.position.y,
            teethTransformationData[teethNumberMinusOne]
              .ToothRotationCenter[2] + parentUpper.parent.position.z,
          );
          cube1.position.set(
            cube.position.x -
              teethTransformationData[teethNumberMinusOne].Up[0] * 10,
            cube.position.y -
              teethTransformationData[teethNumberMinusOne].Up[1] * 10,
            cube.position.z -
              teethTransformationData[teethNumberMinusOne].Up[2] * 10,
          );

          cloneHolder.position.set(
            cube.position.x,
            cube.position.y,
            cube.position.z,
          );
          cloneHolder.lookAt(cube1.position);
          item.children[3].attach(cloneHolder);

          cloneHolder.updateMatrixWorld();
          cloneHolder.visible = true;
        }
      });
      parentLower.children.forEach(item => {
        let teethNumberMinusOne = item.name.split("_")[2].substring(1) - 1;
        if (teethTransformationData[teethNumberMinusOne]) {
          cloneHolder = rootAnchor.scene.children[0].clone();
          cloneHolder.scale.set(100, 100, 100);
          cloneHolder.name = "rootAnchor";
          cloneHolder.updateMatrixWorld();
          const geometry = new THREE.BoxGeometry(1, 1, 1);
          const material = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
          const cube = new THREE.Mesh(geometry, material);
          const cube1 = new THREE.Mesh(geometry, material);
          cube.position.set(
            teethTransformationData[teethNumberMinusOne]
              .ToothRotationCenter[0] + parentLower.parent.position.x,
            teethTransformationData[teethNumberMinusOne]
              .ToothRotationCenter[1] + parentLower.parent.position.y,
            teethTransformationData[teethNumberMinusOne]
              .ToothRotationCenter[2] + parentLower.parent.position.z,
          );
          cube1.position.set(
            cube.position.x -
              teethTransformationData[teethNumberMinusOne].Up[0] * 10,
            cube.position.y -
              teethTransformationData[teethNumberMinusOne].Up[1] * 10,
            cube.position.z -
              teethTransformationData[teethNumberMinusOne].Up[2] * 10,
          );

          cloneHolder.position.set(
            cube.position.x,
            cube.position.y,
            cube.position.z,
          );
          cloneHolder.lookAt(cube1.position);
          item.children[3].attach(cloneHolder);

          cloneHolder.rotateZ(3.14);
          cloneHolder.visible = true;
        }
      });
    });
  }
  function getRayCollisionPoint(x, y, camera, collisionList) {
    const intersection = {
      intersects: false,
      point: new THREE.Vector3(),
      normal: new THREE.Vector3(),
    };
    let mouseHelper = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 10),
      new THREE.MeshNormalMaterial(),
    );
    const mouse = new THREE.Vector2();
    let intersects = [];
    let raycaster = new THREE.Raycaster();
    mouse.x = (x / window.innerWidth) * 2 - 1;
    mouse.y = -(y / window.innerHeight) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);

    raycaster.intersectObjects(collisionList, false, intersects);
    let teethHolder;
    if (intersects.length > 0) {
      teethHolder = intersects[0];
      if (teethHolder.object != selectedAttachment.object.parent) {
        return;
      }
      const p = teethHolder.point;
      mouseHelper.position.copy(p);
      intersection.point.copy(p);
      const n = teethHolder.face.normal.clone();
      n.transformDirection(teethHolder.object.matrixWorld);
      n.multiplyScalar(10);
      n.add(teethHolder.point);

      intersection.normal.copy(teethHolder.face.normal);
      mouseHelper.lookAt(n);
      line.visible = true;
      setPositionToWorld(
        selectedAttachment.object,
        new THREE.Vector3(p.x, p.y, p.z),
        n,
      );
      // selectedAttachment.object.position.set(p.x, p.y, p.z);
      //positions.setXYZ(1, n.x, n.y, n.z);
      //selectedAttachment.needsUpdate = true;

      intersection.intersects = true;

      intersects.length = 0;
    } else {
      intersection.intersects = false;
    }
  }

  //const normalContactThreshold = -10;
  const heavyContactThreshold = 0;
  if (!rootsAreCreated && teethTransformationData) {
    setTimeout(() => {
      createRootAnchors(teethTransformationData);
    }, 1000);
    rootsAreCreated = true;
  }
  if (turnGUIOn) {
    if (guiCounter == 0) {
      guiCounter++;
      const gui = new GUI();
      gui.addColor(conf, "color").onChange(function (values) {
        teethMaterial.color = conf;
        updateState({});

        // heavyContactThreshold = conf.number;
      });
      gui.addColor(conf1, "color").onChange(function (values) {
        attachmentMaterial.specularColor = conf1;
        updateState({});

        // heavyContactThreshold = conf.number;
      });
      gui.addColor(conf5, "color").onChange(function (values) {
        teethMaterial.emissive = conf5;
        updateState({});
        teethMaterial.needsUpdate = true;

        // heavyContactThreshold = conf.number;
      });
      gui.addColor(conf2, "color").onChange(function (values) {
        gumMaterial.color = conf2;

        updateState({});
        // heavyContactThreshold = conf.number;
      });
      gui.addColor(conf3, "color").onChange(function (values) {
        gumMaterial.emissive = conf3;
        updateState({});

        // heavyContactThreshold = conf.number;
      });

      gui.addColor(conf4, "color").onChange(function (values) {
        gumMaterial.specularColor = conf4;
        updateState({});

        // heavyContactThreshold = conf.number;
      });
    }
  }

  const bgLineHeavyContactMaterial = new THREE.LineBasicMaterial({
    color: "red",
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1,
    blending: THREE.CustomBlending,
  });

  const bgLineNormalContactMaterial = new THREE.LineBasicMaterial({
    color: "blue",
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1,
    blending: THREE.CustomBlending,
    linewidth: 1,
  });

  let center = { x: 0, y: 0, z: 0 };

  function setPositionToWorld(object, position, lookAtPosition) {
    if (true) {
      // lookAtPosition.add(selectedAttachmentOffset);
      let box3 = new THREE.Box3().setFromObject(object);
      const point = new THREE.Vector3(); // attachment center
      box3.getCenter(point);

      const movementAmount = position.sub(point); // amount needed to move to reach the target position

      let objectPos = object.position.clone(); // local attachment pos
      let objectWorldPos = new THREE.Vector3(); // world attachment pos
      object.getWorldPosition(objectWorldPos);
      point.sub(objectWorldPos);

      object.position.set(0, 0, 0);
      object.lookAt(lookAtPosition);

      object.position.set(objectPos.x, objectPos.y, objectPos.z);
      let toWorldOffset = objectPos.sub(objectWorldPos); //

      let box31 = new THREE.Box3().setFromObject(object);
      const point2 = new THREE.Vector3(); // attachment center
      box31.getCenter(point2);

      const newObjectWorldPosition = point.add(objectWorldPos); // distance btw attachment and pivot point
      newObjectWorldPosition.add(toWorldOffset);

      point2.sub(objectWorldPos);
      newObjectWorldPosition.sub(point2);
      newObjectWorldPosition.add(movementAmount);

      // objectWorldPos = point2;
      // point2.add(toWorldOffset);

      // objectWorldPos.sub(t); // move the world pos by attachment distance
      // objectWorldPos.add(toWorldOffset); // make the world position to local pos

      //objectWorldPos.add(selectedAttachmentOffset);
      //objectWorldPos.add(t.x, t.y, t.z);
      object.position.set(
        newObjectWorldPosition.x,
        newObjectWorldPosition.y,
        newObjectWorldPosition.z,
      );
      once++;
    }
  }

  function checkIntersection(x, y, camera, collisionList) {
    const intersection = {
      intersects: false,
      point: new THREE.Vector3(),
      normal: new THREE.Vector3(),
    };
    let mouseHelper = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 10),
      new THREE.MeshNormalMaterial(),
    );
    const mouse = new THREE.Vector2();
    let intersects = [];
    let raycaster = new THREE.Raycaster();
    mouse.x = (x / window.innerWidth) * 2 - 1;
    mouse.y = -(y / window.innerHeight) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);

    raycaster.intersectObjects(collisionList, false, intersects);
    let attachmentHolder;
    if (intersects.length > 0) {
      attachmentHolder = intersects[0];
      selectedAttachment = attachmentHolder;
      const p = attachmentHolder.point;
      mouseHelper.position.copy(p);
      intersection.point.copy(p);
      const n = attachmentHolder.face.normal.clone();
      n.transformDirection(attachmentHolder.object.matrixWorld);
      n.multiplyScalar(10);
      n.add(attachmentHolder.point);

      intersection.normal.copy(attachmentHolder.face.normal);
      mouseHelper.lookAt(n);

      intersection.intersects = true;

      intersects.length = 0;
      selectedAttachmentOffset = selectedAttachment.object.position
        .clone()
        .sub(p)
        .clone();
    } else {
      intersection.intersects = false;
    }
  }

  function checkIntersection2(x, y, camera, collisionList) {
    const intersection = {
      intersects: false,
      point: new THREE.Vector3(),
      normal: new THREE.Vector3(),
    };
    let mouseHelper = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 10),
      new THREE.MeshNormalMaterial(),
    );
    const mouse = new THREE.Vector2();
    let intersects = [];
    let raycaster = new THREE.Raycaster();
    mouse.x = (x / window.innerWidth) * 2 - 1;
    mouse.y = -(y / window.innerHeight) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);

    raycaster.intersectObjects(collisionList, false, intersects);
    let attachmentHolder;
    if (intersects.length > 0) {
      attachmentHolder = intersects[0];
      selectedAttachment = attachmentHolder;
      const p = attachmentHolder.point;
      mouseHelper.position.copy(p);
      intersection.point.copy(p);
      const n = attachmentHolder.face.normal.clone();
      var ngeometry = new THREE.SphereGeometry();

      n.transformDirection(attachmentHolder.object.matrixWorld);
      n.multiplyScalar(10);
      n.add(attachmentHolder.point);

      intersection.normal.copy(attachmentHolder.face.normal);
      mouseHelper.lookAt(n);

      intersection.intersects = true;

      intersects.length = 0;
      selectedAttachmentOffset = selectedAttachment.object.position
        .clone()
        .sub(p)
        .clone();
    } else {
      intersection.intersects = false;
    }
  }
  useEffect(() => {
    console.log("tp load call");
    if (!tploaded) {
      const isAppleIphoneMobileDevice = !isIpad && isAppleMobile();

      groupRef.current.children.forEach((child, i) => {
        if (child.name) {
          if (child.name.includes("st")) {
            centering_group.add(child.clone());
          }
          if (child.children.length > 0) {
            child.children.forEach(teeth => {
              // teeth.geometry = mergeVertices(teeth.geometry);
              if (isAppleIphoneMobileDevice) {
                teeth.material.flatShading = true;
              } else {
                teeth.geometry.computeVertexNormals();
              }

              teeth?.children[0]?.children.forEach(attachment => {
                attachmentsList.push(attachment);
                if (isAppleIphoneMobileDevice) {
                  attachment.material.flatShading = true;
                } else {
                  attachment.geometry.computeVertexNormals();
                }
                // attachment.geometry = mergeVertices(attachment.geometry);
              });
            });
          }
          if (child.geometry) {
            if (isAppleIphoneMobileDevice) {
              child.material.flatShading = true;
            } else {
              child.geometry.computeVertexNormals();
            }
            // child.geometry = mergeVertices(child.geometry);
          }
        }
      });

      const aabb = new THREE.Box3();
      aabb.setFromObject(centering_group);
      center = new THREE.Vector3(0, 0, 0);
      aabb.getCenter(center);

      setCenterVec({ x: center.x, y: center.y, z: center.z });

      groupRef.current.position.set(-center.x, -center.y, -center.z);
      occlusalMainGroup.position.copy(groupRef.current.position);

      BJGroup.position.set(-center.x, -center.y, -center.z);
      groupRef.current.updateMatrixWorld();
      // if (
      //   true &&
      //   groupRef.current
      //   // Object.values(GLBData[0].lower?.gum).length > 0 &&
      //   // Object.values(GLBData[0].upper?.gum).length > 0
      // ) {
      //   PrepareTeethMovementPath(groupRef, GLBData);
      //   calculateTeethPositions(GLBData.length - 1, groupRef);
      // }
      addRemoveAttachment(scene, perspectiveCameraRef, groupRef.current, {
        x: center.x,
        y: center.y,
        z: center.z,
      });
    }
  }, [tploaded]);
  const [centerVec, setCenterVec] = useState({ x: 0, y: 0, z: 0 });
  useEffect(() => {
    ReflectNumberingToggle();
  }, [
    currentStep,
    toggle.numbering,
    toggle.showUpper,
    toggle.showLower,
    dentalNotation,
  ]);
  const { content } = useCustomTranslation();

  const getContent = (group, key) => {
    return content(`other.${group}.${key}`);
  };
  const getContent3DControls = (group, key) => {
    return content(`controls.${group}.${key}`);
  };
  //console.log("teeth IPR is ready, process time:", Date.now() - t);
  useEffect(() => {
    //let t = Date.now();
    if (!isIPRPlaced) {
      placeIPRBetweenTeeth(groupRef, toggle, iprOverLays);
      isIPRPlaced = true;
    }
  }, [
    currentStep,
    toggle.showUpper,
    toggle.showLower,
    iprOverLays,
    toggle.startRevision,
    toggle.compareModifications,
  ]);

  function RenderCurrentStep() {
    const startTime = Date.now();
    if (!meshGroup) {
      console.log("empty mesh group");
      return;
    }
    let isActive = false;
    const lastStepNumber = maxStepNumber * 2;

    for (let index = 0; index < lastStepNumber + 2; index += 2) {
      isActive = index === currentStep * 2;

      meshGroup.children[index].visible = isActive && toggle.showUpper;
      meshGroup.children[index + 1].visible = isActive && toggle.showLower;
    }

    if (groupRef2.current) {
      groupRef2.current.children[lastStepNumber].visible = toggle.showUpper;
      groupRef2.current.children[lastStepNumber + 1].visible = toggle.showLower;
    }

    if (meshGroup.children[lastStepNumber + 2]) {
      meshGroup.children[lastStepNumber + 2].visible =
        toggle.superImpose && toggle.showUpper;
      meshGroup.children[lastStepNumber + 2 + 1].visible =
        toggle.superImpose && toggle.showLower;
    }
  }
  function ReflectNumberingToggle() {
    if (!meshGroup) {
      console.log("mesh group is empty");
      return;
    }
    //upper
    meshGroup.children[currentStep * 2].children.forEach(tooth => {
      tooth.children[1].visible = toggle.numbering;
    });
    //lower
    meshGroup.children[currentStep * 2 + 1].children.forEach(tooth => {
      tooth.children[1].visible = toggle.numbering;
    });
    if (!groupRef2?.current) {
      return;
    }

    const maxStepNumber = groupRef2.current.children.length - 1;
    groupRef2.current.children[maxStepNumber].children.forEach(tooth => {
      if (tooth.children[1]) {
        tooth.children[1].visible = toggle.numbering;
      }
    });

    groupRef2.current.children[maxStepNumber - 1].children.forEach(tooth => {
      if (tooth.children[1]) {
        tooth.children[1].visible = toggle.numbering;
      }
    });
    // groupRef2.current.children.forEach(gum => {
    //   if (gum.name.includes("gum")) {
    //     gum.children.forEach(tooth => {
    //       tooth.children[1].visible = toggle.numbering;
    //     });
    //   }
    // });
  }
  function DrawContact(mesh1, mesh2, isSplit, step) {
    // const step = parseInt(mesh1.name.split("_")[1].substring(2, 5));
    const occGroupIndex = isSplit ? 1 : 0;
    let results;

    if (
      step === globalMaxStepNumber ||
      !occlusalSceneController.isOccReady(step, occGroupIndex)
    ) {
      results = occlusalAreaManager.createContactLines(mesh1, mesh2);
    }

    if (results) {
      occlusalSceneController.attachOcclusalToStep(
        results,
        step,
        occGroupIndex,
      );
    }
    occlusalSceneController.setCurrentVisibleChild(step, occGroupIndex);
  }
  let wastedTime = 0;

  function newDrawContanct(mesh1, mesh2, isForced, isFlip = false) {
    const startTime = Date.now();

    if (isFlip) {
      [mesh1, mesh2] = [mesh2, mesh1];
    }

    if (!isForced && mesh1.isOcclusalCalculated) {
      return;
    }
    if (
      // !areBoxCollidingWithDepth(
      //   mesh1,
      //   mesh2,
      //   0.1 /* 0.1 first coloring depth */,
      // ) &&
      !areColliding(mesh1, mesh2)
    ) {
      return;
    }

    // test(isFlip, mesh1, mesh2);

    // runGPU(mesh1, mesh2);

    heatMapManager.generateHeatmap(isFlip, mesh1, mesh2);
    // gpuCalc4(isFlip, mesh1, mesh2);

    // test(isFlip, mesh1, mesh2, scene, camera, renderer);
  }

  function areBoxCollidingWithDepth(mesh1, mesh2, depthDistance) {
    const box1 = new THREE.Box3().setFromObject(mesh1); // Get bounding box for the first object
    const box2 = new THREE.Box3().setFromObject(mesh2); // Get bounding box for the second object

    // Check if the bounding boxes intersect
    return box1.intersectsBox(box2) && box2.max.y - box1.min.y > depthDistance;
  }
  function PrepareMesh(GLBData, ponticsData) {
    let toothCopyCacher;
    if (
      (GLBData[0].upper.teeth.length !== 0 &&
        GLBData[0].upper.teeth[0].children.length === 0) ||
      (GLBData[0].lower.teeth.length !== 0 &&
        GLBData[0].lower.teeth[0].children.length === 0)
    ) {
      console.log("GLB not ready");
      return;
    }
    let modelGroup = new THREE.Group();
    let gumHolder, toothHolder;
    GLBData.forEach((gingiva, stepNumber) => {
      gumHolder = new THREE.Group();
      if (gingiva.upper.gum?.name) {
        gumHolder = gingiva.upper.gum.clone();
        disposeObject(gingiva.upper.gum);
        gumHolder.castShadow = true;
        gumHolder.receiveShadow = true;
        gumHolder.material = gumMaterial;
        gumHolder.name = gingiva.upper.gum.name;
        gumHolder.visible = stepNumber == currentStep ? true : false;
        let toothNumber;
        gingiva.upper.teeth.forEach((tooth, toothIndex) => {
          toothHolder = tooth;
          toothHolder.castShadow = true;
          toothHolder.receiveShadow = true;
          toothHolder.name = tooth.name;

          toothNumber = parseInt(
            toothHolder.name.split("_")[2].substring(1, 4),
          );

          if (ponticsData[toothNumber]) {
            toothHolder.material = ponticsTeethMaterial;
            toothHolder.userData["isPontics"] = true;
          } else {
            toothHolder.material = teethMaterial;
          }

          tooth.children[0].children.forEach(attachment => {
            attachment.castShadow = true;
            attachment.receiveShadow = true;
            attachment.name = attachment.name + "_att";
            attachment.visible = true;
            attachment.material = attachmentMaterial;
          });
          toothCopyCacher = toothHolder.clone();
          disposeObject(toothHolder);
          teethMeshObject[toothCopyCacher.name] = toothCopyCacher;
          gumHolder.add(toothCopyCacher);
        });
      }
      modelGroup.add(gumHolder);
      gumHolder = new THREE.Group();

      if (gingiva.lower.gum?.name) {
        gumHolder = gingiva.lower.gum.clone();
        disposeObject(gingiva.lower.gum);
        gumHolder.castShadow = true;
        gumHolder.receiveShadow = true;
        gumHolder.material = gumMaterial;
        gumHolder.name = gingiva.lower.gum.name;
        gumHolder.visible = stepNumber == currentStep ? true : false;
        let toothNumber;
        gingiva.lower.teeth.forEach((tooth, toothIndex) => {
          toothHolder = tooth;
          toothHolder.castShadow = true;
          toothHolder.receiveShadow = true;
          toothHolder.name = tooth.name;

          toothNumber = parseInt(
            toothHolder.name.split("_")[2].substring(1, 4),
          );

          if (ponticsData[toothNumber]) {
            toothHolder.material = ponticsTeethMaterial;
          } else {
            toothHolder.material = teethMaterial;
          }
          tooth.children[0].children.forEach(attachment => {
            attachment.castShadow = true;
            attachment.receiveShadow = true;
            attachment.name = attachment.name + "_att";
            attachment.visible = true;
            attachment.material = attachmentMaterial;
          });
          toothCopyCacher = toothHolder.clone();
          disposeObject(toothHolder);
          teethMeshObject[toothCopyCacher.name] = toothCopyCacher;
          gumHolder.add(toothCopyCacher);
        });
      }
      modelGroup.add(gumHolder);
    });
    //test
    const superImposeMaterial = new THREE.MeshStandardMaterial();
    superImposeMaterial.transparent = true;
    superImposeMaterial.color.set(0x0000e1);
    superImposeMaterial.opacity = 0.4;
    superImposeMaterial.flatShading = false;
    superImposeMaterial.polygonOffset = true;
    superImposeMaterial.polygonOffsetFactor = -0.025;
    superImposeMaterial.depthWrite = true;
    gumHolder = new THREE.Group();
    gumHolder.visible = false;
    gumHolder.name = "Super_Impose_st0_upper";
    GLBData[0].upper.teeth.forEach(tooth => {
      if (tooth.userData["isPontics"]) {
        return;
      }
      toothHolder = tooth.clone();
      toothHolder.material = superImposeMaterial;
      toothHolder.name = "super_" + tooth.name;
      gumHolder.add(toothHolder);
    });
    modelGroup.add(gumHolder);
    gumHolder = new THREE.Group();
    gumHolder.name = "Super_Impose_st0_lower";
    GLBData[0].lower.teeth.forEach(tooth => {
      if (tooth.userData["isPontics"]) {
        return;
      }
      toothHolder = tooth.clone();
      toothHolder.material = superImposeMaterial;
      toothHolder.name = "super_" + tooth.name;
      gumHolder.add(toothHolder);
    });
    gumHolder.visible = false;
    modelGroup.add(gumHolder);

    return modelGroup;
  }

  useEffect(() => {
    if (scene) {
      Controller.setScene(scene);
    }
    if (camera) {
      Controller.setCamera(camera);
    }
    if (setAutoSave) {
      Controller.setAutoSave = setAutoSave.setAutoSave;
    }
    // if (controlsRef) Controller.setControls(controlsRef);
  }, [scene, camera, setAutoSave]);
  const handleSelectTooth = event => {
    if (!toggle.show3DControls) return;
    selectTooth(
      event,
      perspectiveCameraRef,
      raycaster,
      pointer,
      [
        ...groupRef.current.children[globalCurrentStep * 2].children,
        ...groupRef.current.children[globalCurrentStep * 2 + 1].children,
      ],
      teethMaterial,
      scene,
      centerVec,
      setControls3D,
      selectedTooth,
      selectedAttachmentType,
      selectedAttachment,
      numberingOverLays,
      GLBData,
      model,
      setModel,
      groupRef,
      toggle.showUpper,
      toggle.showLower,
      logs,
      setLogs,
      setScene,
      attachmentArray,
      setAutoSave.setAutoSave,
    );
  };

  useEffect(() => {
    if (!toggle.show3DControls) return;

    canvas.current?.addEventListener("pointerdown", handleSelectTooth, false);
    return () => {
      canvas.current.removeEventListener(
        "pointerdown",
        handleSelectTooth,
        false,
      );
    };
  }, [
    selectedAttachmentType,
    centerVec,
    toggle.show3DControls,
    toggle.showUpper,
    toggle.showLower,
    logs,
  ]);
  const [tpBiteJump, setTpBiteJump] = useState([]);
  const [tpWithoutBiteJump, setTpWithoutBiteJump] = useState([]);
  const [tpWithoutBiteJumpWithMod, setTpWithoutBiteJumpWithMod] = useState([]);

  const [iprOverLaysWithBiteJump, setIprOverLaysWithBiteJump] = useState(
    JSON.parse(JSON.stringify(iprOverLays)),
  );
  const [iprOverLaysWithOutBiteJump, setIprOverLaysWithOutBiteJump] = useState(
    JSON.parse(JSON.stringify(iprOverLays)),
  );
  useLayoutEffect(() => {
    Object.values(iprOverLaysWithOutBiteJump[0] || {}).forEach(iprs => {
      iprs.pop(); // Removes the last element from each array
    });
    Object.values(iprOverLaysWithOutBiteJump[1] || {}).forEach(iprs => {
      iprs.pop(); // Removes the last element from each array
    });
    Object.values(iprOverLaysWithBiteJump[0] || {}).forEach(iprs => {
      iprs.splice(-2, 1); // Removes the last element from each array
    });
    Object.values(iprOverLaysWithBiteJump[1] || {}).forEach(iprs => {
      iprs.splice(-2, 1); // Removes the last element from each array
    });
  }, []);

  useLayoutEffect(() => {
    if (!BJGroup) {
      BJGroup = new THREE.Group();
      BJGroup.visible = false;
      BJGroup.name = "BiteJumpGroup";
    }
    // return;
    const mesh2 = groupRef.current;
    const mesh3 = groupRef2.current;
    // Helper function to compute normals for a named mesh
    const computeNormals = (mesh, name) => {
      const object = mesh.getObjectByName(name);
      if (!object) return;

      object.geometry.computeVertexNormals();
      object.children.forEach(child => {
        child.geometry.computeVertexNormals();
        child.children[0]?.children.forEach(attachment => {
          attachment.geometry.computeVertexNormals();
        });
      });
      return object;
    };

    const biteJumpsStep = Math.max(
      Number(biteJumpsSteps[0]),
      Number(biteJumpsSteps[1]),
    );
    // Setting the BiteJump objects if they exist
    const upperGum = computeNormals(mesh2, `u_st${biteJumpsStep}_gum`);
    const lowerGum = computeNormals(mesh2, `l_st${biteJumpsStep}_gum`);

    if (upperGum || lowerGum) {
      setTpBiteJump([upperGum, lowerGum]);
      BJGroup.attach(upperGum);
      BJGroup.attach(lowerGum);
      setTpBiteJump([upperGum, lowerGum]);
      // Remove these objects from mesh2
      mesh2.remove(upperGum);
      mesh2.remove(lowerGum);

      if (mesh3) {
        // Similarly remove from mesh3

        mesh3.remove(mesh3.getObjectByName(`u_st${Number(biteJumpsStep)}_gum`));
        mesh3.remove(mesh3.getObjectByName(`l_st${Number(biteJumpsStep)}_gum`));
      }
    }

    let withHolder0, withHolder1;
    if (tpWithoutBiteJump.length < 2 && maxStepNumber > 0) {
      setTpWithoutBiteJump([
        mesh2.children[maxStepNumber * 2],
        mesh2.children[maxStepNumber * 2 + 1],
      ]);
      const isAppleIphoneMobileDevice = !isIpad && isAppleMobile();

      if (!isAppleIphoneMobileDevice) {
        PrepareTeethMovementPath(groupRef, GLBData); // no need
        calculateTeethPositions(
          GLBData.length - 1 - (globalBiteJump ? 1 : 0),
          groupRef,
        ); // no need
      }

      withHolder0 = mesh2.children[maxStepNumber * 2].clone();
      withHolder1 = mesh2.children[maxStepNumber * 2 + 1].clone();
      setTpWithoutBiteJumpWithMod([withHolder0, withHolder1]);
      tpWithoutBiteJumpWithMod2.push(withHolder0);
      tpWithoutBiteJumpWithMod2.push(withHolder1);

      let test = {};
      test.current = {};
      test.current.children = [withHolder0, withHolder1];
      if (!isAppleIphoneMobileDevice) {
        calculateTeethPositions(0, test); // no need
      }
      scene.updateMatrixWorld(true);
    }

    if (!BJGroup.parent) {
      scene.attach(BJGroup);
    }
  }, [biteJumpsSteps, maxStepNumber, tpWithoutBiteJump]);
  useEffect(() => {
    if (toggle.startRevision) {
      setToggle("biteJump", false);
    }
  }, [toggle.startRevision]);
  useEffect(
    () => {
      let tpBiteJumpClone0, tpBiteJumpClone1;
      const group =
        (toggle.split || isComparingMode) && groupRef2.current
          ? groupRef2
          : groupRef;

      if ((!tpBiteJump.length > 0 && isBJ) || !tpWithoutBiteJump.length > 0) {
        return;
      }
      if (isComparingMode) {
        if (toggle.biteJump && isBJ) {
        } else {
          tpBiteJumpClone0 = tpWithoutBiteJumpWithMod[0];
          tpBiteJumpClone1 = tpWithoutBiteJumpWithMod[1];
          tpBiteJumpClone0.parent = groupRef2.current;
          tpBiteJumpClone0.visible = true;
          groupRef2.current.children[maxStepNumber * 2] = tpBiteJumpClone0;

          tpBiteJumpClone1.parent = groupRef2.current;
          tpBiteJumpClone1.visible = true;
          groupRef2.current.children[maxStepNumber * 2 + 1] = tpBiteJumpClone1;
          groupRef.current.children[maxStepNumber * 2] = tpWithoutBiteJump[0];
          groupRef.current.children[maxStepNumber * 2 + 1] =
            tpWithoutBiteJump[1];
        }
      } else if (toggle.show3DControls) {
        tpBiteJumpClone0 = tpWithoutBiteJumpWithMod[0];
        tpBiteJumpClone1 = tpWithoutBiteJumpWithMod[1];
        tpBiteJumpClone0.parent = groupRef.current;
        tpBiteJumpClone0.visible = true;
        groupRef.current.children[maxStepNumber * 2] = tpBiteJumpClone0;

        tpBiteJumpClone1.parent = groupRef.current;
        tpBiteJumpClone1.visible = true;
        groupRef.current.children[maxStepNumber * 2 + 1] = tpBiteJumpClone1;
        groupRef2.current.children[maxStepNumber * 2] = isBJ
          ? tpBiteJump[0]
          : tpBiteJumpClone0.clone();
        groupRef2.current.children[maxStepNumber * 2 + 1] = isBJ
          ? tpBiteJump[1]
          : tpBiteJumpClone1.clone();
      } else if (toggle.split) {
        // normal viewing

        if (toggle.biteJump && isBJ) {
          tpBiteJumpClone0 = tpBiteJump[0];
          tpBiteJumpClone1 = tpBiteJump[1];

          tpBiteJumpClone0.parent = groupRef2.current;
          tpBiteJumpClone0.visible = true;
          groupRef2.current.children[maxStepNumber * 2] = tpBiteJumpClone0;

          tpBiteJumpClone1.parent = groupRef2.current;
          tpBiteJumpClone1.visible = true;
          groupRef2.current.children[maxStepNumber * 2 + 1] = tpBiteJumpClone1;
          groupRef.current.children[maxStepNumber * 2] = tpWithoutBiteJump[0];
          groupRef.current.children[maxStepNumber * 2 + 1] =
            tpWithoutBiteJump[1];
        } else {
          tpBiteJumpClone0 = tpWithoutBiteJump[0];
          tpBiteJumpClone1 = tpWithoutBiteJump[1];

          tpBiteJumpClone0.parent = groupRef2.current;
          tpBiteJumpClone0.visible = true;
          groupRef2.current.children[maxStepNumber * 2] = tpBiteJumpClone0;

          tpBiteJumpClone1.parent = groupRef2.current;
          tpBiteJumpClone1.visible = true;
          groupRef2.current.children[maxStepNumber * 2 + 1] = tpBiteJumpClone1;
          groupRef.current.children[maxStepNumber * 2] = isBJ
            ? tpBiteJump[0]
            : tpBiteJumpClone0.clone();
          groupRef.current.children[maxStepNumber * 2 + 1] = isBJ
            ? tpBiteJump[1]
            : tpBiteJumpClone1.clone();
        }
      } else {
        if (toggle.biteJump && isBJ) {
          tpBiteJumpClone0 = tpBiteJump[0];
          tpBiteJumpClone1 = tpBiteJump[1];

          tpBiteJumpClone0.parent = groupRef.current;
          tpBiteJumpClone0.visible = currentStep === maxStepNumber;
          groupRef.current.children[maxStepNumber * 2] = tpBiteJumpClone0;

          tpBiteJumpClone1.parent = groupRef.current;
          tpBiteJumpClone1.visible = currentStep === maxStepNumber;
          groupRef.current.children[maxStepNumber * 2 + 1] = tpBiteJumpClone1;

          groupRef2.current.children[maxStepNumber * 2] = tpWithoutBiteJump[0];
          groupRef2.current.children[maxStepNumber * 2 + 1] =
            tpWithoutBiteJump[1];
        } else {
          tpBiteJumpClone0 = tpWithoutBiteJump[0];
          tpBiteJumpClone1 = tpWithoutBiteJump[1];

          tpBiteJumpClone0.parent = groupRef.current;
          tpBiteJumpClone0.visible = currentStep === maxStepNumber;
          groupRef.current.children[maxStepNumber * 2] = tpBiteJumpClone0;

          tpBiteJumpClone1.parent = groupRef.current;
          tpBiteJumpClone1.visible = currentStep === maxStepNumber;
          groupRef.current.children[maxStepNumber * 2 + 1] = tpBiteJumpClone1;

          groupRef2.current.children[maxStepNumber * 2] = isBJ
            ? tpBiteJump[0]
            : tpBiteJumpClone0.clone();
          groupRef2.current.children[maxStepNumber * 2].visible = false;
          groupRef2.current.children[maxStepNumber * 2 + 1] = isBJ
            ? tpBiteJump[1]
            : tpBiteJumpClone1.clone();
          groupRef2.current.children[maxStepNumber * 2 + 1].visible = false;
        }
      }
      return;

      if (!isComparingMode) {
        tpBiteJumpClone0 = toggle.biteJump
          ? tpBiteJump[0].clone()
          : tpWithoutBiteJump[0].clone();
        tpBiteJumpClone1 = toggle.biteJump
          ? tpBiteJump[1].clone()
          : tpWithoutBiteJump[1].clone();
      } else {
        tpBiteJumpClone0 = toggle.biteJump
          ? tpBiteJump[0]
          : tpWithoutBiteJumpWithMod[0];
        tpBiteJumpClone1 = toggle.biteJump
          ? tpBiteJump[1]
          : tpWithoutBiteJumpWithMod[1];
      }

      if (true) {
        tpBiteJumpClone0.parent = group.current;
        tpBiteJumpClone0.visible =
          toggle.split || currentStep === maxStepNumber;
        group.current.children[maxStepNumber * 2] = tpBiteJumpClone0;

        tpBiteJumpClone1.parent = group.current;
        tpBiteJumpClone1.visible =
          toggle.split || currentStep === maxStepNumber;
        group.current.children[maxStepNumber * 2 + 1] = tpBiteJumpClone1;
      } else if (false) {
        if (!tpWithoutBiteJump.length > 0) {
          console.log("issues");
          return;
        }
        const group = groupRef2;
        let h1 = tpWithoutBiteJump[0].clone();
        let h2 = tpWithoutBiteJump[1].clone();

        h1.parent = group.current;
        h1.visible = toggle.split || currentStep === maxStepNumber;
        group.current.children[maxStepNumber * 2] = h1;

        h2.parent = group.current;
        h2.visible = toggle.split || currentStep === maxStepNumber;
        group.current.children[maxStepNumber * 2 + 1] = h2;
      }
      return;
      groupRef2?.current?.children?.forEach(gum => {
        if (gum.name.includes("gum")) {
          gum.children.forEach(tooth => {
            tooth.children[1].visible = toggle.numbering;
          });
        }
      });
    },
    // console.log(groupRef.current);
    [
      toggle.biteJump,
      toggle.hasBiteJump,
      maxStepNumber,
      tpBiteJump,
      tpWithoutBiteJump,
      toggle.split,
      currentStep,
      toggle.show3DControls,
      isComparingMode,
    ],
  );
  useEffect(() => {
    const startTime = Date.now();
    RenderCurrentStep();
    if (occlusalAreaManager) {
      if (!toggle.occ) {
        occlusalSceneController.turnOffOcclusal();
      }
    }
    if (toggle.occ) {
      if (occlusalAreaManager) {
        if (currentStep === globalMaxStepNumber) {
          occlusalSceneController.removeOcclusalOfStep(currentStep, 0);
        }
      }
      // if (toggle.show3DControls) {
      //   resetOcclusal3DControls();
      // }
      for (
        let lowerTeethIndex = 0;
        lowerTeethIndex <
        groupRef.current.children[currentStep * 2 + 1].children.length;
        lowerTeethIndex++
      ) {
        let lowerTooth =
          groupRef.current.children[currentStep * 2 + 1].children[
            lowerTeethIndex
          ];
        for (
          let upperTeethIndex = 0;
          upperTeethIndex <
          groupRef.current.children[currentStep * 2].children.length;
          upperTeethIndex++
        ) {
          let upperTooth =
            groupRef.current.children[currentStep * 2].children[
              upperTeethIndex
            ];
          if (heatMapManager) {
            if (
              upperTeethIndex + lowerTeethIndex > 10 &&
              upperTeethIndex + lowerTeethIndex < 21
            ) {
              // exclude far teeth
              newDrawContanct(upperTooth, lowerTooth, false);
              newDrawContanct(upperTooth, lowerTooth, false, true);
            }
          } else {
            DrawContact(lowerTooth, upperTooth, false, currentStep);
          }
        }
      }
      if (toggle.split || toggle.compareModifications) {
        for (
          let t = 0;
          t <
          groupRef2.current.children[(maxStepNumber - 1) * 2 + 1].children
            .length;
          t++
        ) {
          let lowerTooth =
            groupRef2.current.children[(maxStepNumber - 1) * 2 + 1].children[t];
          let lowerToothOriginal =
            groupRef.current.children[(maxStepNumber - 1) * 2 + 1].children[t];

          if (lowerToothOriginal.geometry.boundsTree) {
            lowerToothOriginal.geometry.computeBoundsTree();
          }

          lowerTooth.geometry = lowerToothOriginal.geometry;

          for (
            let i = 0;
            i <
            groupRef2.current.children[(maxStepNumber - 1) * 2].children.length;
            i++
          ) {
            let upperTooth =
              groupRef2.current.children[(maxStepNumber - 1) * 2].children[i];

            let upperToothOriginal =
              groupRef.current.children[(maxStepNumber - 1) * 2].children[i];

            if (upperToothOriginal.geometry.boundsTree) {
              upperToothOriginal.geometry.computeBoundsTree();
            }

            upperTooth.geometry = upperToothOriginal.geometry;

            if (heatMapManager) {
              if (i + t > 10 && i + t < 21) {
                // exclude far teeth
                newDrawContanct(upperTooth, lowerTooth, false);
                newDrawContanct(upperTooth, lowerTooth, false, true);
              }
            } else if (occlusalAreaManager) {
              scene.updateMatrixWorld(true);

              DrawContact(
                lowerToothOriginal,
                upperToothOriginal,
                true,
                currentStep,
              );
            }
          }
        }
      }
    }

    // console.log(Date.now() - startTime);
  }, [
    currentStep,
    toggle.occ,
    toggle.split,
    toggle.compareModifications,
    toggle.showLower,
    toggle.showUpper,
    toggle.superImpose,
    toggle.biteJump,
  ]);
  function resetOcclusal3DControls() {
    for (let jawIndex = 0; jawIndex < 2; jawIndex++) {
      const gum = groupRef.current.children[maxStepNumber * 2 + jawIndex];
      for (let t = 0; t < gum.children.length; t++) {
        const geometry = gum.children[t].geometry;
        geometry.attributes.color = new THREE.BufferAttribute(
          new Float32Array(geometry.attributes.position.array.length).fill(1.0),
          3,
        );
      }
    }
  }

  const names = [
    "att_hbra",
    "att_vbra",
    "att_oblique",
    "att_extrusion",
    "att_biolane",
    "att_root_control",
    "att_rotational",
  ];
  if (meshGroup) {
    return <primitive ref={groupRef} object={meshGroup} />;
  } else return <></>;
}

export default Mesh;
